import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  RecaptchaVerifier, 
  signInWithPhoneNumber 
} from 'firebase/auth';

// Default / fallback Firebase config
const DEFAULT_FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyBCxwdeSfmBIB0PkjVkKUi87tcfi_WV36g',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'mljewellers.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'mljewellers',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'mljewellers.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '911418912735',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:911418912735:web:e6e6b9209a66f8285f1e6a'
};

/**
 * Retrieves the currently active Firebase configuration.
 * Checks localStorage first (for Admin settings overrides), then environment variables.
 */
export const getActiveFirebaseConfig = () => {
  try {
    const saved = localStorage.getItem('ml_firebase_config_v1');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.apiKey && parsed.projectId) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Error reading saved firebase config:', e);
  }

  return DEFAULT_FIREBASE_CONFIG;
};

/**
 * Saves Firebase configuration to localStorage so store admin can configure it from the dashboard.
 */
export const saveFirebaseConfig = (config) => {
  try {
    localStorage.setItem('ml_firebase_config_v1', JSON.stringify(config));
    // Clear instances so new credentials take effect immediately
    firebaseAppInstance = null;
    firebaseAuthInstance = null;
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = null;
    }
    return true;
  } catch (e) {
    console.error('Error saving firebase config:', e);
    return false;
  }
};

/**
 * Checks whether Firebase is properly configured with valid credentials.
 */
export const isFirebaseConfigured = () => {
  const config = getActiveFirebaseConfig();
  return Boolean(
    config && 
    config.apiKey && 
    config.apiKey.length > 10 && 
    config.projectId && 
    !config.apiKey.includes('YOUR_')
  );
};

let firebaseAppInstance = null;
let firebaseAuthInstance = null;

/**
 * Initializes and returns the Firebase Auth instance.
 */
export const getFirebaseAuth = () => {
  const config = getActiveFirebaseConfig();
  if (!config || !config.apiKey || !config.projectId) {
    return null;
  }

  try {
    if (!getApps().length) {
      firebaseAppInstance = initializeApp(config);
    } else {
      firebaseAppInstance = getApp();
    }
    firebaseAuthInstance = getAuth(firebaseAppInstance);
    // Set language code to Hindi / English / browser default
    firebaseAuthInstance.useDeviceLanguage();
    return firebaseAuthInstance;
  } catch (err) {
    console.error('Failed to initialize Firebase Auth:', err);
    return null;
  }
};

/**
 * Sets up the RecaptchaVerifier for Phone OTP Authentication.
 * Supports invisible reCAPTCHA or normal badge.
 */
export const setupRecaptcha = (containerId = 'recaptcha-container', onVerified) => {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error('Firebase Auth is not initialized. Please configure Firebase in Admin Settings or .env.');
  }

  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (e) {
      console.warn('Could not clear existing recaptcha verifier:', e);
    }
    window.recaptchaVerifier = null;
  }

  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(`Recaptcha container element "#${containerId}" not found in DOM.`);
  }

  window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: (response) => {
      if (onVerified) onVerified(response);
    },
    'expired-callback': () => {
      console.warn('Recaptcha expired. Please retry.');
    }
  });

  return window.recaptchaVerifier;
};

/**
 * Sends a real SMS OTP to the given phone number via Firebase Phone Authentication.
 * @param {string} phoneNumber 10-digit Indian phone or international with +
 * @param {RecaptchaVerifier} appVerifier
 * @returns {Promise<ConfirmationResult>}
 */
export const sendFirebaseOtp = async (phoneNumber, appVerifier) => {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error('Firebase Auth is not ready. Please verify your Firebase project setup.');
  }

  let formatted = String(phoneNumber || '').trim();
  const digitsOnly = formatted.replace(/[^0-9]/g, '');

  if (digitsOnly.length === 10) {
    formatted = `+91${digitsOnly}`;
  } else if (!formatted.startsWith('+')) {
    formatted = `+${digitsOnly}`;
  }

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, formatted, appVerifier);
    window.confirmationResult = confirmationResult;
    return confirmationResult;
  } catch (error) {
    console.error('Firebase send OTP error:', error);
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.render().then((widgetId) => {
          if (window.grecaptcha) {
            window.grecaptcha.reset(widgetId);
          }
        });
      } catch (e) {}
    }
    throw error;
  }
};

/**
 * Verifies the 6-digit OTP entered by the user against Firebase confirmation.
 * @param {ConfirmationResult} confirmationResult
 * @param {string} otpCode
 * @returns {Promise<User>}
 */
export const verifyFirebaseOtp = async (confirmationResult, otpCode) => {
  const activeConfirmation = confirmationResult || window.confirmationResult;
  if (!activeConfirmation) {
    throw new Error('No active OTP session found. Please click "Resend OTP".');
  }

  const cleanOtp = String(otpCode || '').trim();
  if (cleanOtp.length < 4) {
    throw new Error('Please enter the complete OTP received.');
  }

  const userCredential = await activeConfirmation.confirm(cleanOtp);
  return userCredential.user;
};
